import React, { Component, useRef, useState } from "reactn";
import sanitize from "sanitize-filename";
import styled from "styled-components";
import { MenuItem, MenuList, Popover, Icon, IconButton } from "@material-ui/core";
import { MoreVert as MoreVertIcon } from "@material-ui/icons";

import { AiImageStyleTypes, AiImageStyles, AiImageModel } from "common/aiConstants";
import { ASSET_RESOLUTION, AssetType } from "common/constants";
import { getStaticUrl } from "js/config";
import WorkspaceController from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import { ds } from "js/core/models/dataService";
import AiGenerationService from "js/core/services/aiGeneration";
import { loadImage } from "js/core/utilities/promiseHelper";
import { downloadFromUrl, trackActivity } from "js/core/utilities/utilities";
import { ShowDialog, ShowDialogAsync, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import { ShowInsufficientCreditsDialog, ShowTooManyRequestsDialog } from "js/react/components/Dialogs/CreditsErrorDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { Notice } from "js/react/components/Notice";
import { BlueButton, UIContainerContext, UIPane, UIPaneContents, UIPaneHeader } from "js/react/components/UiComponents";
import ImageThumbnailGrid from "js/react/views/AddAssets/Components/ImageThumbnailGrid";
import EndTrialDialog from "js/react/views/Billing/dialogs/EndTrialDialog";
import { ImageStyleMenu, ImageStyleOption } from "js/react/views/AIGeneration/components/ImageStyleInput";
import CustomImageStyleDialog from "js/react/views/AIGeneration/dialogs/CustomImageStyleDialog";
import { Popup, PopupPreview, PopupContent } from "js/Components/Popup";
import PresentationEditorController from "js/editor/PresentationEditor/PresentationEditorController";
import { AbsoluteBox } from "js/react/components/LayoutGrid";
import { FlexLayout } from "js/Components/FlexLayout";
import PromptInput from "js/react/views/AIGeneration/components/PromptInput";
import { themeColors } from "js/react/sharedStyles";
import { Gap10 } from "js/react/components/Gap";
import { TextContent } from "js/Components/TextContent";
import * as ApiErrors from "apis/errors";

const logger = getLogger(LogGroup.AI);

const ImageStylePreview = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;

    .MuiIconButton-root {
        opacity: 0;
        background: ${themeColors.ui_blue};
        color: white;
    }

    &:hover { 
        .MuiIconButton-root {
            opacity: 1;
            
            &:hover {
                background: #0e90c1;    
            }
        }
    }
`;

const ThumbnailContainer = styled.div.attrs(({ width, height }) => ({
    style: {
        width: `${width}px`,
        height: `${height}px`
    }
}))`
    position: relative;
`;

const ThumbnailContainerImage = styled.img.attrs(({ showOutline }) => ({
    style: {
        outline: showOutline ? "1px solid #50bbe6" : "none"
    }
}))`
    width: 100%;
`;

const ThumbnailContainerBottomBar = styled.div.attrs(({ visible }) => ({
    style: {
        opacity: visible ? 1 : 0
    }
}))`
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    background: black;
    padding: 5px 10px;
    transition: opacity 300ms;
    display: flex;
    align-items: center;
    justify-content: space-between;

    >span {
        font-weight: 600;
        font-size: 12px;
        line-height: 150%;
        color: #FFFFFF;
        margin-right: 10px;
    }

    >div {
        border-radius: 50%;
        min-width: 30px;
        width: 30px;
        height: 30px;
        background: white;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;

        &:hover {
            color: #fefefe;
        }

        >svg {
            color: #666666;
        }
    }
`;

function Thumbnail({ width, height, readUrl, attribution, onSelect, onSetSearchQuery }) {
    const ref = useRef();

    const [isPopoverOpen, setPopoverOpen] = useState(false);
    const [isHoveringOverContainer, setIsHoveringOverContainer] = useState(false);

    const handleOpenPopover = () => {
        setPopoverOpen(true);
    };

    const handleClosePopover = () => {
        setPopoverOpen(false);
        setIsHoveringOverContainer(false);
    };

    const handleMouseEnter = () => {
        setIsHoveringOverContainer(true);
    };

    const handleMouseLeave = () => {
        if (!isPopoverOpen) {
            setIsHoveringOverContainer(false);
        }
    };

    const handleDownload = () => {
        downloadFromUrl(readUrl, sanitize(attribution));
        handleClosePopover();
    };

    const handleSetSearchQuery = () => {
        onSetSearchQuery(attribution);
        handleClosePopover();
    };

    return (<>
        <ThumbnailContainer
            width={width}
            height={height}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <ThumbnailContainerImage
                src={readUrl}
                onClick={onSelect}
                showOutline={isHoveringOverContainer}
            />
            <ThumbnailContainerBottomBar
                visible={isHoveringOverContainer}
            >
                <span>{attribution}</span>
                <div
                    ref={ref}
                    onClick={handleOpenPopover}
                >
                    <MoreVertIcon />
                </div>
            </ThumbnailContainerBottomBar>
        </ThumbnailContainer>
        <Popover
            open={isPopoverOpen}
            anchorEl={ref.current}
            anchorOrigin={{
                vertical: "top",
                horizontal: "center",
            }}
            transformOrigin={{
                vertical: "bottom",
                horizontal: "center",
            }}
            onClose={handleClosePopover}
        >
            <MenuList>
                <MenuItem value={0} onClick={handleDownload}>Download Image</MenuItem>
                <MenuItem value={1} onClick={handleSetSearchQuery}>Use this prompt again</MenuItem>
            </MenuList>
        </Popover>
    </>);
}

class AISearchPane extends Component {
    static contextType = UIContainerContext;
    static searchResultsLength = 1;

    constructor(props) {
        super(props);

        const presentationMetadata = PresentationEditorController.presentation?.get("metadata");
        const currentTheme = PresentationEditorController.currentTheme;

        this.state = {
            isLoading: true,
            isSearching: false,
            searchQuery: "",
            searchResults: [],
            creditsBalance: 0,
            workspaceId: AppController.workspaceId,
            isStyleSelectMenuOpen: false,
            imageStyle: (!!presentationMetadata?.imageStyle && presentationMetadata.imageStyle !== "stock") ? presentationMetadata.imageStyle : "theme",
            imageStylePrompt: presentationMetadata?.imageStylePrompt ? presentationMetadata.imageStylePrompt
                : (!!currentTheme && currentTheme.has("imageStylePrompt") ? currentTheme.get("imageStylePrompt") : AiImageStyles[AiImageStyleTypes.MINIMALIST].prompt),
        };

        this.promptInputRef = React.createRef();
        this.thumbnailButtonsRefs = [...new Array(AISearchPane.searchResultsLength)].map(() => React.createRef());
    }

    componentDidMount() {
        (async () => {
            await Promise.all([
                this.loadCreditsBalance(),
                this.loadRecentlyUsedAssets()
            ]);

            // After data loads, wait 2 seconds, then unfocus the input and reveal the page.
            setTimeout(() => {
                if (document.activeElement && typeof document.activeElement.blur === "function") {
                    document.activeElement.blur();
                }
                this.setState({ isLoading: false });
            }, 2000);
        })().catch(err => {
            logger.error(err, "[AISearchPane] load failed");
            this.setState({ isLoading: false });
        });
    }

    async loadCreditsBalance() {
        const { workspaceId } = this.state;
        const creditsBalance = await AiGenerationService.getCreditsBalance(workspaceId);
        this.setState({ creditsBalance });
    }

    async loadRecentlyUsedAssets() {
        const { workspaceId } = this.state;

        const allAssets = await ds.assets.getAssetsByType(AssetType.IMAGE, workspaceId);

        const loadedDalleAssets = [];
        const chunkSize = 20;

        for (let i = 0; i < allAssets.length; i += chunkSize) {
            const chunk = allAssets.slice(i, i + chunkSize);

            await Promise.all(chunk.map(asset => asset.load()));

            for (const asset of chunk) {
                if (asset.get("source") === "dall-e") {
                    loadedDalleAssets.push(asset);
                }
            }

            if (loadedDalleAssets.length >= 100) {
                break;
            }
        }

        const finalAssets = loadedDalleAssets
            .sort((a, b) => {
                const userAssetA = ds.userAssets.get(a.id);
                const userAssetB = ds.userAssets.get(b.id);
                const timeA = userAssetA?.get("lastUsed") ?? userAssetA?.get("modifiedAt");
                const timeB = userAssetB?.get("lastUsed") ?? userAssetB?.get("modifiedAt");
                return timeB - timeA;
            })
            .slice(0, 100);

        const searchResults = await Promise.all(finalAssets.map(async asset => {
            const readUrl = await asset.getURL(ASSET_RESOLUTION.MEDIUM);
            let assetId = asset.id;
            let attribution = asset.get("attribution");

            const imageStylePrompt = Object.values(AiImageStyles).find(({ prompt }) => attribution.includes(prompt))?.prompt || attribution.match(new RegExp(", in a \"" + "(.*)" + "\" style"))?.[1];
            if (imageStylePrompt) {
                attribution = attribution.substring(0, attribution.indexOf(", in a"));
            }

            return { readUrl, assetId, attribution };
        }));

        this.setState({ searchResults });
    }

    doImageSearch = async searchQuery => {
        const { workspaceId, imageStyle, imageStylePrompt } = this.state;

        this.setState({ isSearching: true, searchQuery });

        try {
            let { response: { assets }, remainingCreditsBalance: creditsBalance } = await AiGenerationService.generateImages({
                prompt: searchQuery,
                imageStyle,
                imageStylePrompt,
                imagesCount: AISearchPane.searchResultsLength,
            });

            // Preload all images for proper caching and smoother ux
            await Promise.all(assets.map(({ readUrl }) => loadImage(readUrl)));

            // Remove the style prompt from the attribution
            assets = assets.map(asset => {
                let attribution = asset.attribution;
                const imageStylePrompt = Object.values(AiImageStyles).find(({ prompt }) => attribution.includes(prompt))?.prompt || attribution.match(new RegExp(", in a \"" + "(.*)" + "\" style"))?.[1];
                if (imageStylePrompt) {
                    attribution = attribution.substring(0, attribution.indexOf(", in a"));
                }
                return { ...asset, attribution };
            });

            this.setState(({ searchResults }) => ({
                isSearching: false,
                searchResults: [...assets, ...searchResults],
                creditsBalance
            }));
            trackActivity("Image", "Generate", null, null, {
                asset_id: assets[0].assetId,
                prompt: searchQuery,
                custom_style_type: (!imageStyle || imageStyle === "stock") ? null
                    : imageStyle === "custom" ? "prompt" : "preset",
                custom_style_type_prompt: (!imageStyle || imageStyle === "stock") ? null
                    : imageStylePrompt,
                model_name: AiImageModel.GEMINI_2_5_FLASH_PREVIEW
            });
        } catch (err) {
            logger.error(err, "[AISearchPane] doImageSearch() failed", { workspaceId, searchQuery });

            if (err instanceof ApiErrors.PaymentRequiredError) {
                ShowInsufficientCreditsDialog(() => this.loadCreditsBalance());
            } else if (err instanceof ApiErrors.TooManyRequestsError) {
                ShowTooManyRequestsDialog();
            } else {
                ShowErrorDialog({
                    error: "Sorry, we couldn't process your request",
                    message: err.message
                });
            }

            this.setState({
                isSearching: false,
                searchQuery: "",
                imageStylePrompt: "",
            });
        }
    }

    handleSelectImage = async assetId => {
        const { addAssetCallback, handleConfirm } = this.props;

        const asset = await ds.assets.getAssetById(assetId, AssetType.IMAGE);
        addAssetCallback(asset);

        handleConfirm();
    }

    handleSelectImageStyle = async newImageStyle => {
        // reset the image style if it's none
        if (newImageStyle === "none") {
            this.setState({ imageStyle: newImageStyle, imageStylePrompt: "" });
            PresentationEditorController.presentation.update({ metadata: { ...PresentationEditorController.presentation.get("metadata"), imageStyle: newImageStyle } });
            await PresentationEditorController.presentation.updatePromise;
            return;
        }

        // open custom image style dialog if new image style is custom
        if (newImageStyle === "custom") {
            this.setState({ imageStyle: newImageStyle, imageStylePrompt: "" });
            PresentationEditorController.presentation.update({ metadata: { ...PresentationEditorController.presentation.get("metadata"), imageStyle: newImageStyle } });
            await PresentationEditorController.presentation.updatePromise;

            return ShowDialog(CustomImageStyleDialog, {
                theme: PresentationEditorController.currentTheme,
                imageStyle: this.state.imageStyle,
                imageStylePrompt: this.state.imageStylePrompt,
                hideThemeImageStyle: true,
                onSuccess: (imageStyle, imageStylePrompt) => {
                    this.setState({ imageStyle, imageStylePrompt });
                }
            });
        }

        // set the image style to the theme's image style prompt (defaulting to minimalist) if it's theme
        if (newImageStyle === "theme") {
            this.setState({ imageStyle: newImageStyle, imageStylePrompt: PresentationEditorController.currentTheme.get("imageStylePrompt") ?? AiImageStyles[AiImageStyleTypes.MINIMALIST].prompt });
            PresentationEditorController.presentation.update({ metadata: { ...PresentationEditorController.presentation.get("metadata"), imageStyle: newImageStyle } });
            await PresentationEditorController.presentation.updatePromise;
            return;
        }

        // set the image style to the new ai image style prompt
        if (Object.values(AiImageStyleTypes).includes(newImageStyle)) {
            this.setState({
                imageStyle: newImageStyle,
                imageStylePrompt: AiImageStyles[newImageStyle].prompt
            });
            PresentationEditorController.presentation.update({ metadata: { ...PresentationEditorController.presentation.get("metadata"), imageStyle: newImageStyle } });
            await PresentationEditorController.presentation.updatePromise;
        }
    }

    handleStyleSelected = selectedStyle => {
        this.setState(({ searchQuery }) => ({ searchQuery: `${searchQuery}, ${selectedStyle}` }));
    }

    handleSetSearchQuery = searchQuery => {
        this.setState({ searchQuery });
    }

    handleEnterKeyPress = searchQuery => {
        this.doImageSearch(searchQuery);
    }

    handleEndTrial = () => {
        ShowDialogAsync(EndTrialDialog)
            .then(() => this.loadCreditsBalance());
    };

    render() {
        const {
            isLoading,
            isSearching,
            searchResults,
            searchQuery,
            imageStyle,
        } = this.state;

        const uiPaneContentsStyle = { paddingTop: 20, position: "relative" };

        return (
            <UIPane>
                <FetchingClickShield visible={isLoading} backgroundColor="white" />

                <FlexLayout padding={20} background="#eee" minHeight={220}>
                    <FlexLayout gap={10} padding={"0 20px"} background="white" width="fill">
                        <FlexLayout gap={10} direction="column" grow={1}>
                            <Gap10 />
                            <FlexLayout height={28}>
                                <TextContent header>Generate an image with AI</TextContent>
                            </FlexLayout>
                            <PromptInput
                                ref={this.promptInputRef}
                                prompt={searchQuery}
                                onPromptChange={this.handleSetSearchQuery}
                                onEnterKeyPress={this.handleEnterKeyPress}
                                promptPlaceholder="Describe your image in detail..."
                                hideExamplePrompts
                                inputContainerHeight={72}
                                inputDisabled={isSearching}
                                multiline={true}
                            />
                        </FlexLayout>
                        <FlexLayout direction="column" grow={0}>
                            <Popup>
                                <PopupPreview>
                                    <FlexLayout gap={10} justify="space-between" direction="column">
                                        <Gap10 />
                                        <FlexLayout height={27}>
                                            <TextContent center popupLabel>Image Style</TextContent>
                                        </FlexLayout>
                                        <ImageStylePreview>
                                            <ImageStyleOption imageStyle={imageStyle} iconOnly />
                                            <AbsoluteBox>
                                                <IconButton><Icon>edit</Icon></IconButton>
                                            </AbsoluteBox>
                                        </ImageStylePreview>
                                        <ImageStyleOption imageStyle={imageStyle} labelOnly />
                                    </FlexLayout>
                                </PopupPreview>

                                <PopupContent>
                                    {closePopup => (
                                        <ImageStyleMenu
                                            allowedImageStyles={["theme", ...Object.values(AiImageStyleTypes), "custom", "none"]}
                                            selectedImageStyle={imageStyle}
                                            onImageStyleSelected={style => {
                                                this.handleSelectImageStyle(style);
                                                closePopup();
                                            }}
                                        />
                                    )}
                                </PopupContent>
                            </Popup>
                        </FlexLayout>
                    </FlexLayout>
                </FlexLayout>

                <UIPaneContents style={uiPaneContentsStyle}>
                    {isSearching &&
                        <Notice
                            image={getStaticUrl("/images/bai-bot/bai-bot-happy.png")}
                            imageWidth={100}
                            showProgressBar
                            title="Generating your image... Please wait."
                        />
                    }
                    {!isSearching && <>
                        {searchResults.length > 0 &&
                            <ImageThumbnailGrid columns={4}>
                                {searchResults.map(({ assetId, attribution, imageStylePrompt, readUrl }) => (
                                    <Thumbnail
                                        key={assetId}
                                        readUrl={readUrl}
                                        attribution={attribution}
                                        imageStyle={imageStyle}
                                        imageStylePrompt={imageStylePrompt}
                                        onSelect={() => this.handleSelectImage(assetId)}
                                        onSetSearchQuery={searchQuery => {
                                            this.promptInputRef.current.typePrompt(searchQuery).then(() => {
                                                this.handleSetSearchQuery(searchQuery);
                                            });
                                        }}
                                    />
                                ))}
                            </ImageThumbnailGrid>
                        }
                        {searchResults.length === 0 &&
                            <Notice
                                image={getStaticUrl("/images/ai-search/ai-search.gif")}
                                imageWidth={400}
                                title="Describe what you are looking for and let DesignerBot generate an image unique to your story."
                            />
                        }
                    </>}
                </UIPaneContents>

                <FlexLayout width="fill" padding={20} align="center" justify="flex-end">
                    <BlueButton
                        disabled={isSearching || searchQuery.trim().length === 0}
                        onClick={() => this.doImageSearch(searchQuery)}
                    >Generate Image</BlueButton>
                </FlexLayout>
            </UIPane>
        );
    }
}

export default AISearchPane;
